import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAnnualTimetableCells } from '../../services/annualTimetableService';
import { resolveMergedTimetableCellsByKey } from '../../utils/timetableCellsByKeyResolve';
import { buildAnnualCellMap } from '../../utils/annualTimetable';
import type { AnnualTimetableCell } from '../../types/domain';
import { queryClient } from '../queryClient';
import { invalidateAnnualProgressTimetableCellsBulkQuery as invalidateBulkFromCacheUpdates } from '../cacheUpdates';
import { lsInitialDataOptions } from './lsInitialData';

export type UseAnnualProgressTimetableCellsBulkQueryParams = {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeIds: string[];
  enabled?: boolean;
  forceNetwork?: boolean;
};

function gradeIdsKey(gradeIds: string[]): string {
  return [...gradeIds].sort().join('\u0001');
}

function readBulkTimetableInitialData(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[]
): { found: boolean; cellsByKey: Record<string, AnnualTimetableCell> } {
  const cellsByKey = resolveMergedTimetableCellsByKey({
    workspaceId,
    academicYear,
    termNumber,
    gradeIds,
  });
  if (Object.keys(cellsByKey).length > 0) {
    return { found: true, cellsByKey };
  }
  return { found: false, cellsByKey: {} };
}

export function useAnnualProgressTimetableCellsBulkQuery(params: UseAnnualProgressTimetableCellsBulkQueryParams) {
  const {
    workspaceId,
    academicYear,
    termNumber,
    gradeIds,
    enabled = true,
    forceNetwork = false,
  } = params;
  const ids = gradeIds.filter(Boolean);
  const gKey = gradeIdsKey(ids);
  const ls = readBulkTimetableInitialData(workspaceId, academicYear, termNumber, ids);

  const query = useQuery({
    queryKey: ['school', workspaceId, 'annualTimetableCellsBulk', academicYear, termNumber, gKey] as const,
    enabled: enabled && !!workspaceId.trim() && ids.length > 0,
    ...lsInitialDataOptions(ls.found && !forceNetwork, ls.cellsByKey),
    queryFn: async (): Promise<Record<string, AnnualTimetableCell>> => {
      const rows = await getAnnualTimetableCells(workspaceId, academicYear, termNumber, ids, {
        forceNetwork,
      });
      return buildAnnualCellMap(rows);
    },
  });
  /**
   * forceNetwork 는 쿼리 키에 없어서(staleTime 무한) 값만 바뀌면 다시 읽지 않았다.
   * 켜지는 순간 한 번만 서버에서 다시 읽는다(시간표 저장 이벤트 때만 — 폴링 없음).
   */
  const { refetch } = query;
  const queryEnabled = enabled && !!workspaceId.trim() && ids.length > 0;
  useEffect(() => {
    if (!forceNetwork || !queryEnabled) return;
    void refetch();
  }, [forceNetwork, queryEnabled, refetch]);
  return query;
}

export function annualProgressTimetableCellsBulkQueryKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[]
) {
  return ['school', workspaceId, 'annualTimetableCellsBulk', academicYear, termNumber, gradeIdsKey(gradeIds)] as const;
}

export function invalidateAnnualProgressTimetableCellsBulkQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[]
): void {
  invalidateBulkFromCacheUpdates(workspaceId, academicYear, termNumber, gradeIds);
}
